import type { BundledFontId, TextHorizontalAlign, TextVerticalAlign, TextWhiteSpace } from '../sdf/shapeEngine.js';

/** `strings` is a list of texts (the engine's `PortType::Strings`). */
export type PortType = 'float' | 'vec2' | 'vec3' | 'vec4' | 'bool' | 'string' | 'strings' | 'flow' | 'collection' | 'record' | 'bounds' | 'transform' | 'shape' | 'size' | 'rects';
export type OutputPortType = 'float' | 'vec3' | 'vec4' | 'shape' | 'bool' | 'string' | 'collection' | 'record';
export type ValuePortType = Exclude<PortType, 'strings' | 'flow' | 'collection' | 'record' | 'bounds' | 'transform' | 'shape' | 'size' | 'rects'>;
export type ValueSubtype = 'default' | 'color' | 'factor' | 'angle' | 'distance' | 'pixels' | 'percentage' | 'em';

/**
 * Storage unit of a value, orthogonal to its `subtype` (presentation). The
 * dragger converts storage→display based on the (subtype, unit) pair. When
 * omitted, each subtype falls back to its canonical unit (see dragField).
 */
export type ValueUnit = 'radians' | 'degrees' | 'fraction' | 'percent' | 'px' | 'em';

export interface PortRange {
  min: number;
  max: number;
  step: number;
  subtype?: ValueSubtype;
  unit?: ValueUnit;
}

export interface PortDef {
  name: string;
  type: PortType;
  label?: string;
  /** Value range hint — rendered as inline dragger next to the port. */
  range?: PortRange;
  /** When true, unconnected port stays undefined instead of getting defaultForType. */
  optional?: boolean;
}

// ── Declarative UI row schema ────────────────────────────────

/** Reference to a port defined in inputs/outputs by name. */
export interface UIPortRow {
  kind: 'port';
  ref: string;
  io?: 'in' | 'out';
}

export interface UISelectRow {
  kind: 'select';
  field: string;
  label: string;
  options: readonly UISelectOption[];
  default: string;
}

export interface UISelectOption {
  label: string;
  value: string;
}

export interface UICheckboxRow {
  kind: 'checkbox';
  field: string;
  label: string;
  default: boolean;
}

export interface UIDraggerRow {
  kind: 'dragger';
  field: string;
  label: string;
  min: number;
  max: number;
  step: number;
  subtype?: ValueSubtype;
  unit?: ValueUnit;
}

export interface UISectionRow {
  kind: 'section';
  label: string;
}

export interface UITextRow {
  kind: 'text';
  field: string;
  label?: string;
  placeholder?: string;
  valueTypeField?: string;
}

export interface UIDataSourceRow {
  kind: 'data-source';
  field: string;
  label: string;
}
export interface UIShapeAssetRow { kind: 'shape-asset'; field: string; label: string }
export interface UIActionRow { kind: 'action'; action: 'convert-shape-to-path'; label: string }
export interface UIPathSummaryRow { kind: 'path-summary' }

export interface UIDataFieldRow {
  kind: 'data-field';
  field: string;
  sourceField: string;
  label: string;
}
export interface UIQueryGraphRow { kind: 'query-graph'; field: string; label: string }
export interface UIQueryOutputRow { kind: 'query-output'; field: string; queryField: string; label: string }
export interface UIQueryFieldRow { kind: 'query-field'; field: string; label: string }
export interface UIRecordFieldRow { kind: 'record-field'; field: string; label: string }

export interface UITextareaRow {
  kind: 'textarea';
  field: string;
  placeholder?: string;
  rows?: number;
}

export type UIRow = UIPortRow | UISelectRow | UICheckboxRow | UIDraggerRow | UISectionRow | UITextRow | UITextareaRow | UIDataSourceRow | UIShapeAssetRow | UIActionRow | UIPathSummaryRow | UIDataFieldRow | UIQueryGraphRow | UIQueryOutputRow | UIQueryFieldRow | UIRecordFieldRow;

export interface Size {
  w: number;
  h: number;
}

export interface LayoutRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ShapePortValue {
  readonly kind: 'shape';
  readonly key: string;
}

export type OutputValue = number | number[];
/** Universal port value — includes layout types (Size, LayoutRect[], null). */
export type NodePortValue = OutputValue | boolean | string | ShapePortValue | null | Size | LayoutRect[];
export type NamedInputs = Record<string, NodePortValue>;
/** Type-safe inputs keyed by literal port names from a readonly port definition array. */
export type TypedInputs<I extends readonly PortDef[]> = { readonly [K in I[number]['name']]: NodePortValue };
/** One Path contour as a param override carries it (`geometry:<node>.contours`): the authored arc spline — anchors, `smooth` flags, and per segment an explicit bulge or `null` for a derived one. */
export interface ContourOverride {
  anchors: number[];
  smooth?: (0 | 1 | boolean)[];
  bulges?: (number | null)[];
  closed?: boolean;
  [extra: string]: unknown;
}
/** Params also carry strings (override-first text/align, `geometry:<node>.<field>`) and Path contours (`geometry:<node>.contours`). */
export type ParamValue = OutputValue | string | ContourOverride[];
export type ParamStore = Record<string, ParamValue>;

export type NodeKind =
  | 'OnEvent'
  | 'EventField'
  | 'Constant'
  | 'Branch'
  | 'Set'
  | 'SetRecordField'
  | 'Toggle'
  | 'Reset'
  | 'Equal'
  | 'NotEqual'
  | 'IsNull'
  | 'Coalesce'
  | 'And'
  | 'Or'
  | 'Not'
  | 'Greater'
  | 'GreaterEqual'
  | 'Less'
  | 'LessEqual'
  | 'BoolToNumber'
  | 'FormatNumber'
  | 'FormatDuration'
  | 'Concat'
  | 'ListLength'
  | 'PickText'
  | 'JoinText'
  | 'TextEdit'
  | 'Value'
  | 'Float'
  | 'Vector2'
  | 'Vector3'
  | 'Vector4'
  | 'Color'
  | 'Time'
  | 'Pointer'
  | 'Hover'
  | 'Pressed'
  | 'PressOutside'
  | 'Scroll'
  | 'Reveal'
  | 'State'
  | 'Input'
  | 'Lookup'
  | 'Query'
  | 'RecordField'
  | 'Source'
  | 'Filter'
  | 'Select'
  | 'Sort'
  | 'Limit'
  | 'Find'
  | 'Field'
  | 'Count'
  | 'Sum'
  | 'Average'
  | 'Spring'
  | 'Hold'
  | 'Previous'
  | 'Stopwatch'
  | 'SamplePoint'
  | 'Viewport'
  | 'LayerSize'
  | 'Distance'
  | 'MiterDistance'
  | 'SmoothDistance'
  | 'ArcLength'
  | 'ShapeNormal'
  | 'ShapeCoverage'
  | 'Theme'
  | 'LayoutBounds'
  | 'ContourBounds'
  | 'AdjustBounds'
  | 'Add'
  | 'Subtract'
  | 'Multiply'
  | 'Divide'
  | 'Negate'
  | 'Absolute'
  | 'Sin'
  | 'Cos'
  | 'Atan2'
  | 'Power'
  | 'SquareRoot'
  | 'Min'
  | 'Max'
  | 'Clamp'
  | 'Mix'
  | 'MixColor'
  | 'CoverageContrast'
  | 'Switch'
  | 'BlendColor'
  | 'TextRunColor'
  | 'Smoothstep'
  | 'Length'
  | 'Fraction'
  | 'Floor'
  | 'Ceil'
  | 'Round'
  | 'Modulo'
  | 'Vec2'
  | 'Vec3'
  | 'Vec4'
  | 'SplitVec2'
  | 'SplitVec3'
  | 'Dot'
  | 'Cross'
  | 'Normalize'
  | 'AddVectors'
  | 'SubtractVectors'
  | 'ScaleVector'
  | 'Illuminance'
  | 'SplitBounds'
  | 'BoundsSize'
  | 'BoundsCenter'
  | 'PointInBounds'
  | 'Translate'
  | 'Rotate'
  | 'Scale'
  | 'Circle'
  | 'Rect'
  | 'Union'
  | 'Difference'
  | 'Intersect'
  | 'Xor'
  | 'SmoothUnion'
  | 'SmoothSubtract'
  | 'SmoothIntersect'
  | 'ValueNoise'
  | 'Perlin'
  | 'Fbm'
  | 'Turbulence'
  | 'Colorize'
  | 'Shape'
  | 'Path'
  | 'Text'
  | 'ShapeTransform'
  | 'ShapeTranslate'
  | 'ShapeRotate'
  | 'ShapeScale'
  | 'ShapeOffset'
  | 'ShapeAlongPath'
  | 'ShapeLattice'
  | 'ShapeBones'
  | 'TextSelection'
  | 'TextCaret'
  | 'Mask'
  | 'Blur'
  | 'Bands'
  | 'Output';

export type NodeCategory = 'source' | 'math' | 'vector' | 'bounds' | 'transform' | 'sdf' | 'special' | 'output' | 'event' | 'control' | 'command' | 'logic';
export type NodeSubcategory = 'shapes' | 'boolean' | 'smooth-boolean' | 'glyph' | 'arithmetic' | 'trigonometry' | 'range' | 'blend' | 'value';

/** Structural minimum shared by RenderNodeDef and LayoutNodeDef — used for rendering and layout utilities. */
export interface NodeDefBase {
  label: string;
  cat: NodeCategory | NodeCategory[];
  subcat?: NodeSubcategory;
  inputs: PortDef[] | ((node: GraphNode) => PortDef[]);
  outputs: PortDef[] | ((node: GraphNode) => PortDef[]);
  defaults?: Record<string, unknown>;
  ui?: UIRow[];
  extraUIRows?: number;
  minWidth?: number;
}

export interface EmptyNodeData extends Record<string, unknown> {}

export interface ValueNodeData extends Record<string, unknown> {
  name: string;
  exposed: boolean;
  valueType: ValuePortType;
  subtype: ValueSubtype;
  hasMin: boolean;
  hasMax: boolean;
  min: number;
  max: number;
  value0: number;
  value1: number;
  value2: number;
  value3: number;
}

export type FloatSubtype = 'default' | 'factor' | 'angle' | 'distance' | 'pixels' | 'percentage' | 'em';

export interface FloatValueNodeData extends Record<string, unknown> {
  name: string;
  exposed: boolean;
  subtype: FloatSubtype;
  hasMin: boolean;
  hasMax: boolean;
  min: number;
  max: number;
  value0: number;
}

export interface VecValueNodeData extends Record<string, unknown> {
  name: string;
  exposed: boolean;
  value0: number;
  value1: number;
  value2: number;
  value3: number;
}

export interface ColorNodeData extends Record<string, unknown> {
  name: string;
  exposed: boolean;
  value0: number;
  value1: number;
  value2: number;
  value3: number;
}

export interface OutputNodeData extends Record<string, unknown> {
  inputName: string;
  inputType: OutputPortType;
  displayType?: OutputPortType;
}

export type BlendColorMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'add'
  | 'subtract'
  | 'difference'
  | 'min'
  | 'max';

export interface BlendColorNodeData extends Record<string, unknown> {
  mode: BlendColorMode;
  opacity: number;
}

/** `contrast` is what the node reads when nothing is wired to its port. */
export interface CoverageContrastNodeData extends Record<string, unknown> {
  contrast: number;
}

export type SwitchValueType = 'float' | 'vec2' | 'vec3' | 'vec4';

export interface SwitchNodeData extends Record<string, unknown> {
  valueType: SwitchValueType;
  /** Number of value inputs. Stored as a string by the select control. */
  count: number;
}

export interface ScaleNodeData extends Record<string, unknown> {
  density: number;
}

export interface ShapePrimitiveNodeData extends Record<string, unknown> {}

export interface AdjustBoundsNodeData extends Record<string, unknown> {
  direction: string;
  mode: string;
  value: number;
  horizontal: number;
  vertical: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ScaleXYNodeData extends Record<string, unknown> {
  sx: number;
  sy: number;
  linked: boolean;
}

export interface ShapeScaleNodeData extends Record<string, unknown> {
  default: number;
}

export interface RectNodeData extends ShapePrimitiveNodeData {
  cornerRadius: number;
}

export interface SmoothNodeData extends ScaleNodeData {
  smoothness: number;
}

export interface SeedNodeData extends Record<string, unknown> {
  seed: number;
}

export interface FractalNoiseNodeData extends SeedNodeData {
  octaves: number;
  lacunarity: number;
  gain: number;
}

export interface SdfMaskNodeData extends Record<string, unknown> {
  width: number;
}

export interface BlurNodeData extends Record<string, unknown> {
  radius: number;
  intensity: number;
}

export interface ShapeNodeData extends Record<string, unknown> {
  shapeRef: string;
  width: number;
  height: number;
  fit: 'contain' | 'cover';
  alignX: 'start' | 'center' | 'end';
  alignY: 'start' | 'center' | 'end';
}

export interface PathContourData {
  anchors: number[];
  bulges: number[];
  closed: boolean;
}

export interface PathNodeData extends Record<string, unknown> {
  fillRule: 'nonzero' | 'evenodd';
  contours: PathContourData[];
}

export interface SdfTextNodeData extends Record<string, unknown> {
  text: string;
  font: BundledFontId;
  letterSpacing: number;
  autoWidth: boolean;
  width: number;
  autoHeight: boolean;
  height: number;
  whiteSpace: TextWhiteSpace;
  horizontalAlign: TextHorizontalAlign;
  verticalAlign: TextVerticalAlign;
  lineHeight: number;
  size: number;
}

export interface RuntimeStateNodeData extends Record<string, unknown> {
  name?: string;
  state?: string;
  scope?: 'global' | 'owner';
  valueType: 'bool' | 'number' | 'string' | 'vec2' | 'vec3' | 'vec4';
}

/** `pattern` is what the node writes the time by when no text is wired to it. */
export interface FormatDurationNodeData extends Record<string, unknown> {
  pattern: string;
}

/** An unwired `decimals` or `unit` port reads the field of its name. */
export interface FormatNumberNodeData extends Record<string, unknown> {
  decimals: number;
  unit: string;
}

export interface PickTextNodeData extends Record<string, unknown> {
  index: number;
}

export interface JoinTextNodeData extends Record<string, unknown> {
  separator: string;
}

/** Laid out as a `Text` with these fields would be; `font` unless one is wired. */
export interface TextEditNodeData extends Record<string, unknown> {
  font: BundledFontId;
}

export interface ScrollNodeData extends Record<string, unknown> {
  axis: 'x' | 'y';
}

export interface ScaleVectorNodeData extends Record<string, unknown> {
  scale: number;
}

/** The smoothing radius, in the field's own units; nought is `Distance`. */
export interface SmoothDistanceNodeData extends Record<string, unknown> {
  k: number;
}

export type ShapeOffsetJoin = 'round' | 'miter' | 'bevel' | 'square';

export interface ShapeOffsetNodeData extends Record<string, unknown> {
  distance: number;
  join: ShapeOffsetJoin;
  miterLimit: number;
}

/**
 * A deformation of a shape -- along a path (`along`), by a lattice (`lattice`),
 * by bones (`bones`) -- as the document writes it. Posed in the native editor;
 * here it is carried as it stands and compiled by the engine.
 */
export interface ShapeDeformNodeData extends Record<string, unknown> {}

/** A text's selection: the text's fields, and the byte range it covers. */
export interface TextSelectionNodeData extends SdfTextNodeData {
  start: number;
  end: number;
}

/** A text's caret: the text's fields, the byte it stands before, its width in pixels. */
export interface TextCaretNodeData extends SdfTextNodeData {
  at: number;
  caretWidth: number;
}

/** `count` bands, each a colour between two stops; the stops are the ports' unwired values. */
export interface BandsNodeData extends Record<string, unknown> {
  count: number;
  distance: number | null;
  widthUnit: string;
}

export interface DataInputNodeData extends Record<string, unknown> {
  name: string;
  valueType: 'bool' | 'number' | 'string' | 'vec2' | 'vec3' | 'vec4';
  default?: boolean | number | string | number[] | null;
}

export interface RecordFieldNodeData extends Record<string, unknown> {
  field: string;
  valueType: 'bool' | 'number' | 'string' | 'vec2' | 'vec3' | 'vec4';
}

export interface NodeDataByKind {
  OnEvent: EmptyNodeData;
  EventField: EmptyNodeData;
  Constant: EmptyNodeData;
  Branch: EmptyNodeData;
  Set: EmptyNodeData;
  SetRecordField: RecordFieldNodeData & { source: string };
  Toggle: EmptyNodeData;
  Reset: EmptyNodeData;
  Equal: EmptyNodeData;
  NotEqual: EmptyNodeData;
  IsNull: EmptyNodeData;
  Coalesce: EmptyNodeData;
  And: EmptyNodeData;
  Or: EmptyNodeData;
  Not: EmptyNodeData;
  Greater: EmptyNodeData;
  GreaterEqual: EmptyNodeData;
  Less: EmptyNodeData;
  LessEqual: EmptyNodeData;
  BoolToNumber: EmptyNodeData;
  FormatNumber: FormatNumberNodeData;
  FormatDuration: FormatDurationNodeData;
  Concat: EmptyNodeData;
  ListLength: EmptyNodeData;
  PickText: PickTextNodeData;
  JoinText: JoinTextNodeData;
  TextEdit: TextEditNodeData;
  Value: ValueNodeData;
  Float: FloatValueNodeData;
  Vector2: VecValueNodeData;
  Vector3: VecValueNodeData;
  Vector4: VecValueNodeData;
  Color: ColorNodeData;
  Time: EmptyNodeData;
  Pointer: EmptyNodeData;
  Hover: EmptyNodeData;
  Pressed: EmptyNodeData;
  PressOutside: EmptyNodeData;
  Scroll: ScrollNodeData;
  Reveal: EmptyNodeData;
  State: RuntimeStateNodeData;
  Input: DataInputNodeData;
  Lookup: DataInputNodeData & {
    source: string;
    field: string;
    keyType: 'bool' | 'number' | 'string';
  };
  Query: DataInputNodeData & { query: string; output: string };
  RecordField: RecordFieldNodeData;
  Source: EmptyNodeData;
  Filter: EmptyNodeData;
  Select: EmptyNodeData;
  Sort: EmptyNodeData;
  Limit: EmptyNodeData;
  Find: EmptyNodeData;
  Field: EmptyNodeData;
  Count: EmptyNodeData;
  Sum: EmptyNodeData;
  Average: EmptyNodeData;
  Spring: EmptyNodeData;
  Hold: EmptyNodeData;
  Previous: EmptyNodeData;
  Stopwatch: EmptyNodeData;
  SamplePoint: EmptyNodeData;
  Viewport: EmptyNodeData;
  LayerSize: EmptyNodeData;
  Distance: EmptyNodeData;
  MiterDistance: EmptyNodeData;
  SmoothDistance: SmoothDistanceNodeData;
  ArcLength: EmptyNodeData;
  ShapeNormal: EmptyNodeData;
  ShapeCoverage: EmptyNodeData;
  Theme: EmptyNodeData;
  LayoutBounds: EmptyNodeData;
  ContourBounds: EmptyNodeData;
  AdjustBounds: AdjustBoundsNodeData;
  Add: EmptyNodeData;
  Subtract: EmptyNodeData;
  Multiply: EmptyNodeData;
  Divide: EmptyNodeData;
  Negate: EmptyNodeData;
  Absolute: EmptyNodeData;
  Sin: EmptyNodeData;
  Cos: EmptyNodeData;
  Atan2: EmptyNodeData;
  Power: EmptyNodeData;
  SquareRoot: EmptyNodeData;
  Min: EmptyNodeData;
  Max: EmptyNodeData;
  Clamp: EmptyNodeData;
  Mix: EmptyNodeData;
  MixColor: EmptyNodeData;
  CoverageContrast: CoverageContrastNodeData;
  Switch: SwitchNodeData;
  BlendColor: BlendColorNodeData;
  TextRunColor: EmptyNodeData;
  Smoothstep: EmptyNodeData;
  Length: EmptyNodeData;
  Fraction: EmptyNodeData;
  Floor: EmptyNodeData;
  Ceil: EmptyNodeData;
  Round: EmptyNodeData;
  Modulo: EmptyNodeData;
  Vec2: EmptyNodeData;
  Vec3: EmptyNodeData;
  Vec4: EmptyNodeData;
  SplitVec2: EmptyNodeData;
  SplitVec3: EmptyNodeData;
  Dot: EmptyNodeData;
  Cross: EmptyNodeData;
  Normalize: EmptyNodeData;
  AddVectors: EmptyNodeData;
  SubtractVectors: EmptyNodeData;
  ScaleVector: ScaleVectorNodeData;
  Illuminance: EmptyNodeData;
  SplitBounds: EmptyNodeData;
  BoundsSize: EmptyNodeData;
  BoundsCenter: EmptyNodeData;
  PointInBounds: EmptyNodeData;
  Translate: EmptyNodeData;
  Rotate: EmptyNodeData;
  Scale: ScaleXYNodeData;
  Circle: ShapePrimitiveNodeData;
  Rect: RectNodeData;
  Union: EmptyNodeData;
  Difference: EmptyNodeData;
  Intersect: EmptyNodeData;
  Xor: EmptyNodeData;
  SmoothUnion: SmoothNodeData;
  SmoothSubtract: SmoothNodeData;
  SmoothIntersect: SmoothNodeData;
  ValueNoise: SeedNodeData;
  Perlin: SeedNodeData;
  Fbm: FractalNoiseNodeData;
  Turbulence: FractalNoiseNodeData;
  Colorize: EmptyNodeData;
  Shape: ShapeNodeData;
  Path: PathNodeData;
  Text: SdfTextNodeData;
  ShapeTransform: EmptyNodeData;
  ShapeTranslate: EmptyNodeData;
  ShapeRotate: EmptyNodeData;
  ShapeScale: ShapeScaleNodeData;
  ShapeOffset: ShapeOffsetNodeData;
  ShapeAlongPath: ShapeDeformNodeData;
  ShapeLattice: ShapeDeformNodeData;
  ShapeBones: ShapeDeformNodeData;
  TextSelection: TextSelectionNodeData;
  TextCaret: TextCaretNodeData;
  Mask: SdfMaskNodeData;
  Blur: BlurNodeData;
  Bands: BandsNodeData;
  Output: OutputNodeData;
}

export type GraphNodeData = NodeDataByKind[NodeKind];

interface GraphNodeBase<K extends NodeKind> {
  id: string;
  type: K;
  x: number;
  y: number;
  data: NodeDataByKind[K];
}

export type GraphNode<K extends NodeKind = NodeKind> = {
  [P in K]: GraphNodeBase<P>
}[K];

export type GraphNodeInit<K extends NodeKind = NodeKind> = {
  id: string;
  type: K;
  x: number;
  y: number;
  data?: Partial<NodeDataByKind[K]> | Record<string, unknown>;
};

export type ValueNode = GraphNode<'Value'>;
export type AnyValueNode = GraphNode<'Float' | 'Vector2' | 'Vector3' | 'Vector4' | 'Color'>;
export type OutputNode = GraphNode<'Output'>;
export type ScaleDataNode = never;
export type SmoothDataNode = GraphNode<'SmoothUnion' | 'SmoothSubtract' | 'SmoothIntersect'>;
export type SeedDataNode = GraphNode<'ValueNoise' | 'Perlin'>;
export type FractalNoiseNode = GraphNode<'Fbm' | 'Turbulence'>;

export interface GraphEdge {
  fromNode: string;
  fromPort: number;
  toNode: string;
  toPort: number;
}

export function arePortTypesCompatible(outType: PortType, inType: PortType, inNodeType?: string): boolean {
  if (outType === inType) return true;
  if (inNodeType === 'Output') {
    if (inType === 'vec3') return outType === 'vec3' || outType === 'vec4';
    if (inType === 'vec4') return outType === 'vec3' || outType === 'vec4';
    if (inType === 'bounds') return outType === 'bounds';
    if (inType === 'transform') return outType === 'transform';
    if (inType === 'shape') return outType === 'shape';
    return outType === 'float';
  }
  return false;
}

const SMOOTH_DATA_NODE_TYPES = new Set([
  'SmoothUnion',
  'SmoothSubtract',
  'SmoothIntersect',
] as const);

const SEED_DATA_NODE_TYPES = new Set([
  'ValueNoise',
  'Perlin',
] as const);

const FRACTAL_NOISE_NODE_TYPES = new Set([
  'Fbm',
  'Turbulence',
] as const);

export function getNodeDataRecord(node: GraphNode): Record<string, unknown> {
  return node.data as Record<string, unknown>;
}

export const VALUE_NODE_TYPES: ReadonlySet<string> = new Set(['Value', 'Float', 'Vector2', 'Vector3', 'Vector4', 'Color']);

/** Kinds laid out as a text is (a text and its marks), so each needs its `font` loaded before it compiles. */
export const TEXT_LAYOUT_NODE_TYPES: ReadonlySet<string> = new Set(['Text', 'TextSelection', 'TextCaret']);

export function isValueNode(node: GraphNode): node is ValueNode | AnyValueNode {
  return VALUE_NODE_TYPES.has(node.type);
}

export function isOutputNode(node: GraphNode): node is OutputNode {
  return node.type === 'Output';
}

export function hasScaleNodeData(_node: GraphNode): _node is ScaleDataNode {
  return false;
}

export function hasSmoothNodeData(node: GraphNode): node is SmoothDataNode {
  return SMOOTH_DATA_NODE_TYPES.has(node.type as SmoothDataNode['type']);
}

export function hasSeedNodeData(node: GraphNode): node is SeedDataNode {
  return SEED_DATA_NODE_TYPES.has(node.type as SeedDataNode['type']);
}

export function hasFractalNoiseData(node: GraphNode): node is FractalNoiseNode {
  return FRACTAL_NOISE_NODE_TYPES.has(node.type as FractalNoiseNode['type']);
}

export function getValueNodeOutputType(node: GraphNode): ValuePortType {
  switch (node.type) {
    case 'Float': return 'float';
    case 'Vector2': return 'vec2';
    case 'Vector3': return 'vec3';
    case 'Vector4': case 'Color': return 'vec4';
    case 'Value': return (node.data as ValueNodeData).valueType as ValuePortType;
    default: return 'float';
  }
}

export function getValueNodeSubtype(node: GraphNode): ValueSubtype {
  if (node.type === 'Color') return 'color';
  if (node.type === 'Float') return (node.data as FloatValueNodeData).subtype ?? 'default';
  if (node.type === 'Value') return (node.data as ValueNodeData).subtype ?? 'default';
  return 'default';
}

export function getValueSubtypeLabel(subtype: ValueSubtype): string {
  switch (subtype) {
    case 'color':
      return 'Color';
    case 'factor':
      return 'Factor';
    case 'angle':
      return 'Angle';
    case 'distance':
      return 'Distance';
    case 'pixels':
      return 'Pixels';
    case 'percentage':
      return 'Percentage';
    case 'em':
      return 'Em';
    default:
      return 'Value';
  }
}

export function getValueNodeComponentCount(type: ValuePortType): 1 | 2 | 3 | 4 {
  if (type === 'float') return 1;
  if (type === 'vec2') return 2;
  if (type === 'vec3') return 3;
  return 4;
}

export function getValueNodeSubtypeOptions(type: ValuePortType): readonly ValueSubtype[] {
  if (type === 'float') return ['default', 'factor', 'angle', 'distance', 'pixels', 'percentage', 'em'];
  if (type === 'vec3' || type === 'vec4') return ['default', 'color'];
  return ['default'];
}

export function getValueNodeDataValue(data: ValueNodeData): OutputValue {
  const { valueType, value0, value1, value2, value3 } = data;
  if (valueType === 'float') return value0;
  if (valueType === 'vec2') return [value0, value1];
  if (valueType === 'vec3') return [value0, value1, value2];
  return [value0, value1, value2, value3];
}

export function getValueNodeBounds(data: Record<string, unknown>): { min: number; max: number; hasSlider: boolean } {
  const min = data.hasMin ? Number(data.min) : Number.NEGATIVE_INFINITY;
  const max = data.hasMax ? Number(data.max) : Number.POSITIVE_INFINITY;
  return {
    min,
    max,
    hasSlider: Boolean(data.hasMin && data.hasMax),
  };
}

export function getValueNodeStoredValue(node: GraphNode): OutputValue {
  const d = node.data as Record<string, unknown>;
  switch (node.type) {
    case 'Float': return (d.value0 as number) ?? 0;
    case 'Vector2': return [(d.value0 as number) ?? 0, (d.value1 as number) ?? 0];
    case 'Vector3': return [(d.value0 as number) ?? 0, (d.value1 as number) ?? 0, (d.value2 as number) ?? 0];
    case 'Vector4': case 'Color': return [(d.value0 as number) ?? 0, (d.value1 as number) ?? 0, (d.value2 as number) ?? 0, (d.value3 as number) ?? 1];
    case 'Value': return getValueNodeDataValue(d as unknown as ValueNodeData);
    default: return 0;
  }
}

export function getOutputNodeInputType(node: GraphNode): OutputPortType {
  if (!isOutputNode(node)) return 'float';
  return node.data.inputType;
}

export function getOutputNodeDisplayType(node: GraphNode): OutputPortType {
  if (!isOutputNode(node)) return 'float';
  return node.data.displayType ?? node.data.inputType;
}

export function applyOutputNodeSlotMetadata(node: GraphNode, slot: 'geometry' | 'material'): void {
  if (!isOutputNode(node)) return;
  if (slot === 'material') {
    node.data.inputName = 'color';
    node.data.inputType = 'vec4';
    node.data.displayType = 'vec4';
    return;
  }

  node.data.inputName = 'shape';
  node.data.inputType = 'shape';
  node.data.displayType = 'shape';
}

export function normalizeGraphNode<K extends NodeKind>(node: GraphNodeInit<K>, defs?: Record<string, NodeDefBase>): GraphNode<K> {
  const def = defs?.[node.type] as { defaults?: Record<string, unknown> } | undefined;
  const authored = (node.data ?? {}) as Record<string, unknown>;
  const data: Record<string, unknown> = def?.defaults ? { ...def.defaults, ...authored } : { ...authored };
  // The codec writes a parameter exposed without a name as `exposed: true`
  // and reads it back with no `name`: the table's default name ('value',
  // 'color') is for a value node that is not exposed, and filling it in here
  // would name the parameter on the next save.
  if (authored.exposed === true && !('name' in authored) && 'name' in data) data.name = '';
  return {
    id: node.id,
    type: node.type,
    x: node.x,
    y: node.y,
    data: data as NodeDataByKind[K],
  };
}

/**
 * A node of a kind the catalogue does not know -- one the engine added after
 * this catalogue was written -- is the engine's to evaluate and the
 * document's to keep: it is carried exactly as it was read, edges and all,
 * never dropped, so a save writes back what the load read.
 */
export function isUncataloguedNode(node: { type: string }, defs: Record<string, NodeDefBase>): boolean {
  return !Object.prototype.hasOwnProperty.call(defs, node.type);
}

/**
 * How the node editor draws a node the catalogue does not know: its kind for
 * a title, no fields, and a socket for every port an edge of the graph names,
 * typed as the port at the edge's other end -- so its wires stay drawn,
 * coloured and attached. A port no edge names is not known here and not drawn.
 */
export function uncataloguedNodeDef(
  node: GraphNode,
  graphNodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  defs: Record<string, NodeDefBase>,
): NodeDefBase {
  const typeAtOtherEnd = (nodeId: string, port: number, io: 'inputs' | 'outputs'): PortType => {
    const other = graphNodes.find(candidate => candidate.id === nodeId);
    const def = other ? defs[other.type] : undefined;
    if (!other || !def) return 'float';
    const declared = def[io];
    const ports = typeof declared === 'function' ? declared(other) : declared;
    return ports[port]?.type ?? 'float';
  };
  const inputs: PortDef[] = [];
  const outputs: PortDef[] = [];
  for (const edge of edges) {
    if (edge.toNode === node.id) {
      inputs[edge.toPort] = { name: `in${edge.toPort}`, label: `Input ${edge.toPort}`, type: typeAtOtherEnd(edge.fromNode, edge.fromPort, 'outputs') };
    }
    if (edge.fromNode === node.id) {
      outputs[edge.fromPort] = { name: `out${edge.fromPort}`, label: `Output ${edge.fromPort}`, type: typeAtOtherEnd(edge.toNode, edge.toPort, 'inputs') };
    }
  }
  // Sockets keep the engine's port numbers: an unwired port before a wired one
  // is drawn too, so the index a socket stands at is the port an edge names.
  const filled = (ports: PortDef[], prefix: 'in' | 'out'): PortDef[] =>
    Array.from(ports, (port, index) => port ?? { name: `${prefix}${index}`, label: `${prefix === 'in' ? 'Input' : 'Output'} ${index}`, type: 'float' });
  return { label: node.type, cat: 'special', inputs: filled(inputs, 'in'), outputs: filled(outputs, 'out') };
}

export function normalizeGraphNodes(nodes: readonly GraphNodeInit[], defs: Record<string, NodeDefBase>): GraphNode[] {
  return nodes
    .map(node => {
      if (isUncataloguedNode(node, defs)) return structuredClone(node) as unknown as GraphNode;
      const n = normalizeGraphNode(node, defs);
      // Migrate old Scale node data: { scale } → { sx, sy, linked }
      if (n.type === 'Scale') {
        const d = n.data as Record<string, unknown>;
        if (d.scale !== undefined && d.sx === undefined) {
          d.sx = d.scale;
          d.sy = d.scale;
        }
      }
      // Migrate polymorphic Value → typed value nodes
      if (n.type === 'Value') {
        const d = n.data as Record<string, unknown>;
        const vt = d.valueType as string;
        const st = d.subtype as string;
        if ((vt === 'vec3' || vt === 'vec4') && st === 'color') {
          (n as { type: string }).type = 'Color';
        } else if (vt === 'float') {
          (n as { type: string }).type = 'Float';
        } else if (vt === 'vec2') {
          (n as { type: string }).type = 'Vector2';
        } else if (vt === 'vec3') {
          (n as { type: string }).type = 'Vector3';
        } else if (vt === 'vec4') {
          (n as { type: string }).type = 'Vector4';
        }
      }
      return n;
    });
}

export function cloneGraphNode<K extends NodeKind>(node: GraphNode<K>): GraphNode<K> {
  return { id: node.id, type: node.type, x: node.x, y: node.y, data: { ...node.data } as NodeDataByKind[K] };
}

export function cloneGraphNodes(nodes: readonly GraphNode[]): GraphNode[] {
  return nodes.map(node => cloneGraphNode(node));
}

export const LAYOUT = {
  HDR: 33,
  PAD: 6,
  ROW: 24,
  VALUE_ROW_GAP: 6,
  PORT_R: 6,
  NODE_MIN_W: 160,
  CHAR_W: 7
};

function visibleUiRows(node: GraphNode, def: NodeDefBase): UIRow[] {
  if (!def.ui) return [];
  const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
  const outputs = typeof def.outputs === 'function' ? def.outputs(node) : def.outputs;
  const portNames = new Set([...inputs, ...outputs].map(port => port.name));
  return def.ui.filter(row => row.kind !== 'port' || portNames.has(row.ref));
}

export function nodeHeight(node: GraphNode, defs: Record<string, NodeDefBase>): number {
  const def = defs[node.type] as NodeDefBase | undefined;
  if (!def) return 80;
  if (isValueNode(node)) {
    let fixedHeights: number[];
    switch (node.type) {
      case 'Float': {
        fixedHeights = [24, 24, 24, 18, 24, 24, 24]; // expose, name, subtype, section(16+2), value, min, max
        break;
      }
      case 'Vector2': fixedHeights = [24, 24, 18, 24, 24]; break; // expose, name, section(16+2), X, Y
      case 'Vector3': fixedHeights = [24, 24, 18, 24, 24, 24]; break; // expose, name, section(16+2), X, Y, Z
      case 'Vector4': fixedHeights = [24, 24, 18, 24, 24, 24, 24]; break; // expose, name, section(16+2), X, Y, Z, W
      case 'Color': fixedHeights = [24, 24, 18, 24]; break; // expose, name, section(16+2), colorpicker+alpha
      default: {
        // Legacy Value node
        const type = (node.data as ValueNodeData).valueType;
        const renderAsColor = (type === 'vec3' || type === 'vec4') && ((node.data as ValueNodeData).subtype ?? 'default') === 'color';
        const subtypeRows = getValueNodeSubtypeOptions(type).length > 1 ? 1 : 0;
        const valueRows = renderAsColor ? 1 : getValueNodeComponentCount(type);
        const rangeRows = type === 'float' ? 2 : 0;
        fixedHeights = [24, 30, 30, ...Array.from({ length: subtypeRows }, () => 30), 16, ...Array.from({ length: valueRows }, () => 24), ...Array.from({ length: rangeRows }, () => 24)];
        break;
      }
    }
    const contentHeight = fixedHeights.reduce((sum, height) => sum + height, 0);
    const rowGaps = Math.max(0, fixedHeights.length - 1) * LAYOUT.VALUE_ROW_GAP;
    return LAYOUT.HDR + LAYOUT.PAD + contentHeight + rowGaps + LAYOUT.PAD;
  }

  if (def.ui) {
    const rows = visibleUiRows(node, def);
    const gaps = Math.max(0, rows.length - 1) * LAYOUT.VALUE_ROW_GAP;
    return LAYOUT.HDR + LAYOUT.PAD + rows.length * LAYOUT.ROW + gaps + LAYOUT.PAD;
  }

  const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
  const outputs = typeof def.outputs === 'function' ? def.outputs(node) : def.outputs;
  const rows = inputs.length + outputs.length + (def.extraUIRows ?? 0);
  const gaps = Math.max(0, rows - 1) * LAYOUT.VALUE_ROW_GAP;
  return LAYOUT.HDR + LAYOUT.PAD + rows * LAYOUT.ROW + gaps + LAYOUT.PAD;
}

export function nodeWidth(node: GraphNode, defs: Record<string, NodeDefBase>): number {
  const def = defs[node.type] as NodeDefBase | undefined;
  if (!def) return LAYOUT.NODE_MIN_W;
  if (isValueNode(node)) return 236;

  if (def.ui) {
    const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
    const outputs = typeof def.outputs === 'function' ? def.outputs(node) : def.outputs;
    const allPorts = [...inputs, ...outputs];
    let minW = LAYOUT.NODE_MIN_W;
    const BASE = 90;
    const CONTROL_W = 50;
    for (const row of visibleUiRows(node, def)) {
      if (row.kind === 'port') {
        const port = allPorts.find(p => p.name === row.ref);
        const len = port ? (port.label ?? port.name).length : row.ref.length;
        minW = Math.max(minW, BASE + len * LAYOUT.CHAR_W);
      } else if (row.kind === 'section') {
        minW = Math.max(minW, BASE + row.label.length * LAYOUT.CHAR_W);
      } else if (row.kind === 'select' || row.kind === 'dragger' || row.kind === 'checkbox') {
        minW = Math.max(minW, BASE + row.label.length * LAYOUT.CHAR_W + CONTROL_W);
      }
    }
    minW = Math.max(minW, BASE + def.label.length * LAYOUT.CHAR_W);
    return Math.max(minW, def.minWidth ?? 0);
  }

  const inputs = typeof def.inputs === 'function' ? def.inputs(node) : def.inputs;
  const outputs = typeof def.outputs === 'function' ? def.outputs(node) : def.outputs;
  const maxLabel = Math.max(
    ...inputs.map(port => port.name.length),
    ...outputs.map(port => port.name.length),
    def.label.length
  );
  return Math.max(LAYOUT.NODE_MIN_W, def.minWidth ?? 0, 120 + maxLabel * LAYOUT.CHAR_W);
}
